/**
 * ★ EACH ARTICLE'S VIEWS, LIKES AND COMMENTS ON ITS CARD - the owner's decision 4 (design 15, "agree
 * on all", 2026-10-01): the prototype's cards carry `👁 3,980 ♡ 76 💬 21`, and `ArticleListItem`
 * carries no figure at all. The figures are the per-article statistics' own, joined in.
 *
 * ONE READ PER PAGE LOAD, NOT ONE PER CARD. `listArticleStats` is read once when the list mounts -
 * no range, so the server's usual period - and each loaded card looks its article up in the answer
 * by `articleId`. A filter change, a "Load more" or a re-read after a row action reads the list
 * again, never this.
 *
 * ★ `limit` IS THE ONE PARAMETER SENT, AND IT IS THE MAXIMUM (100, `pageLimit` in
 * src/modules/analytics/cabinet/dashboard-cabinet.schemas.ts). That read lists EVERY article of the
 * author's, newest CREATED first (article-stats.reader.ts, `listPage`), while the list below is
 * newest EDITED first: at the default 20 an author with a long history - 130 pieces brought into
 * the cabinet at once - would find figures on a handful of cards. One page of 100 covers most
 * authors whole. A card whose article is not in that page shows no figures; nothing is guessed.
 *
 * NEVER A FAKE ZERO. When the read fails, or a card's article is not in it, the card simply shows no
 * figures - no error on a list that loaded, and no `0` standing in for "unknown". Inside a row the
 * metric states keep their meaning (../../features/cabinet/components/metric.tsx): `ok` is the
 * number, `no_data` is a TRUE zero, and `not_reported_yet` is left off the card rather than drawn.
 *
 * ★ A CARD'S VIEWS ARE ALL-TIME, ONE FIGURE (the owner, 2026-10-05: "for the author it is one app,
 * one data - no separate 'earlier views'"). "408 views" on a card is every view the article ever
 * had, from both apps - the row's `allTimeViews`, read by ../../features/cabinet/all-time-views.ts
 * (`oldAppViews` from a server older than the member; no views figure at all when neither is
 * there). Its likes and comments stay the read's period, and the line under the list says which is
 * which (./ArticlesRoute.tsx). Design 14's "1,234 earlier views" beside the views is gone: those
 * views are inside the one figure. Shown, never paid (decision-log row 231).
 *
 * The figures are drawn only on a piece that has been published - a draft has nothing to count.
 * That is ./ArticlesRoute.tsx's decision; this file draws what it is handed.
 */
import { VisuallyHidden } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { Eye, Heart, MessageCircle } from '@moonx/ui';
import { useCabinetApi } from '../../features/cabinet/api';
import type { ArticleStats, MetricTile, PublicId, VideoStats } from '../../features/cabinet/api';
import { allTimeViewsOf } from '../../features/cabinet/all-time-views';
import { MetaPart } from '../../features/cabinet/components';
import { formatCount } from '../../features/cabinet/format';
import { useAsync } from '../../features/cabinet/state';
import { useTranslation } from '../../i18n';
import classes from './articles.module.css';

/** The largest page `listArticleStats` serves. */
export const CARD_FIGURES_LIMIT = 100;

export interface CardFigureSource {
  /** Each article's row, by id; `undefined` until the read answers, and for good if it fails. */
  readonly byArticle: ReadonlyMap<PublicId, ArticleStats> | undefined;
  /** The days the figures cover, as the read named them. Either may be absent on the response. */
  readonly from: string | undefined;
  readonly to: string | undefined;
}

export function useCardFigures(): CardFigureSource {
  const api = useCabinetApi();
  const read = useCallback(
    (signal: AbortSignal) => api.listArticleStats({ limit: CARD_FIGURES_LIMIT }, signal),
    [api],
  );
  const stats = useAsync(read, 'articles:card-figures');
  const page = stats.data;

  return useMemo(() => {
    // Named, because the envelope's `items` is the shared page's and this page's at once.
    const rows: readonly ArticleStats[] = page?.items ?? [];
    return {
      byArticle:
        page === undefined
          ? undefined
          : new Map<PublicId, ArticleStats>(rows.map((row) => [row.articleId, row])),
      from: page?.from,
      to: page?.to,
    };
  }, [page]);
}

/** The number a card may print for a tile: `ok`'s value, `no_data`'s true zero, else nothing. */
function figureOf(tile: MetricTile): number | undefined {
  if (tile.state === 'no_data') {
    return 0;
  }
  return tile.state === 'ok' && tile.value !== undefined && tile.value !== null
    ? tile.value
    : undefined;
}

/** The figure in the text's ink, and its noun for a screen reader: "896 views". */
function Figure({ value, noun }: { value: number; noun: string }) {
  return (
    <>
      <span className={classes.figure}>{formatCount(value)}</span>
      <VisuallyHidden>{` ${noun}`}</VisuallyHidden>
    </>
  );
}

/** One article's figures, as parts of its card's meta line. */
export function CardFigures({ row }: { readonly row: ArticleStats }) {
  const { t } = useTranslation('articles');
  // All-time, the one views figure a card carries (see the header) - never the period's.
  const views = allTimeViewsOf(row);
  const likes = figureOf(row.likes);
  const comments = figureOf(row.comments);

  return (
    <>
      {views === undefined ? null : (
        <MetaPart icon={<Eye size={13} aria-hidden />}>
          <Figure value={views} noun={t('list.cardFigures.views', { count: views })} />
        </MetaPart>
      )}
      {likes === undefined ? null : (
        <MetaPart icon={<Heart size={13} aria-hidden />}>
          <Figure value={likes} noun={t('list.cardFigures.likes', { count: likes })} />
        </MetaPart>
      )}
      {comments === undefined ? null : (
        <MetaPart icon={<MessageCircle size={13} aria-hidden />}>
          <Figure value={comments} noun={t('list.cardFigures.comments', { count: comments })} />
        </MetaPart>
      )}
    </>
  );
}

/**
 * The videos' figures (v1.0.29): one `listVideoStats` read while the Videos list is shown. A
 * video's views are its all-time views, the figure an article's card shows; videos are not in
 * Statistics.
 */
export function useVideoFigures(shown: boolean): ReadonlyMap<PublicId, VideoStats> | undefined {
  const api = useCabinetApi();
  const read = useCallback(
    (signal: AbortSignal) => (shown ? api.listVideoStats(signal) : Promise.resolve(undefined)),
    [api, shown],
  );
  const stats = useAsync(read, `articles:video-figures:${String(shown)}`);
  const items = stats.data?.items;
  return useMemo(
    () =>
      items === undefined
        ? undefined
        : new Map<PublicId, VideoStats>(items.map((row) => [row.articleId, row])),
    [items],
  );
}

/** One video's figures, as parts of its card's meta line. */
export function VideoCardFigures({ row }: { readonly row: VideoStats }) {
  const { t } = useTranslation('articles');
  return (
    <>
      <MetaPart icon={<Eye size={13} aria-hidden />}>
        <Figure value={row.views} noun={t('list.cardFigures.views', { count: row.views })} />
      </MetaPart>
      <MetaPart icon={<Heart size={13} aria-hidden />}>
        <Figure value={row.likes} noun={t('list.cardFigures.likes', { count: row.likes })} />
      </MetaPart>
      <MetaPart icon={<MessageCircle size={13} aria-hidden />}>
        <Figure
          value={row.comments}
          noun={t('list.cardFigures.comments', { count: row.comments })}
        />
      </MetaPart>
    </>
  );
}
